<script setup lang="ts">
import {Button, Card, Column, DataTable, Paginator} from 'primevue';
import {PagedResult} from '@/app/shared/helpers/paged-result.ts';
import {
  DocumentType,
  type Invoice,
  PaymentMethod,
  SessionDetailInvoiceDto
} from '@/api/enterprise-gateway-api.proxy.ts';
import type {PropType} from 'vue';
import {useFormatting} from '@/composables/currency-format.ts';
import {useInvoicingStore} from '@/app/stores/invoicing-store.ts';
import CheckIcon from '@primeicons/vue/check';

const props = defineProps({
  invoices: { type: Array as PropType<SessionDetailInvoiceDto[]>, default: () => ([]) },
  isLoading: { type: Boolean, default: false },
  showReference: { type: Boolean, default: false },
  showCsv: { type: Boolean, default: false },
  service: { type: String, default: '' },
});

const {formatCurrency, formatDate} = useFormatting();
const invoiceStore = useInvoicingStore();


const getInvoiceTypeIcon = (documentType?: DocumentType, paymentMethod?: PaymentMethod): string => {
  return invoiceStore.getInvoiceTypeIcon(documentType, paymentMethod);
};

const getDocumentTypeLabel = (documentType?: DocumentType): string => {
  return invoiceStore.getDocumentTypeLabel(documentType);
};

</script>

<template>
  <aeg-datatable :value="invoices" :loading="isLoading">
    <template #empty>
      Er zijn geen facturen of betalingsbewijzen.
    </template>
    <Column header="Datum" body-style="width: 8em;" >
      <template #body="slotProps">
        {{ formatDate(slotProps?.data?.invoiceDate) }}
      </template>
    </Column>
    <Column field="invoiceNumber" header="Doc. nr." body-style="width: 14em; white-space: nowrap;"></Column>
    <Column header="Service" body-class="aeg-uppercase">
      <template #body="slotProps">
        {{service}}
      </template>
    </Column>
    <Column field="content" header="Document"></Column>
    <Column v-if="showReference" field="customerReference" header="Eigen referentie"></Column>
    <Column body-style="text-align: center; width: 2em;">
      <template #body="slotProps">
        <i :class="getInvoiceTypeIcon(slotProps?.data?.documentType)" style="font-size: 1.125rem;" />
      </template>
    </Column>
    <Column field="totalPrice" header="Bedrag" header-style="direction: rtl; text-align: justify; width: 7em;" body-style="text-align: right;">
      <template #body="slotProps">
        <span :class="{'aeg-invoices-details__topup': slotProps?.data?.isTopUpCredit }">
            {{ slotProps?.data?.isTopUpCredit ? `+&nbsp${formatCurrency(slotProps?.data?.invoiceAmount)}` : formatCurrency(slotProps?.data?.invoiceAmount) }}
        </span>
      </template>
    </Column>
    <Column header="Betaald" body-style="text-align: center; width: 7em;" :pt="{ columnHeaderContent: { class: 'justify-center' }}">
      <template #body="slotProps">
        <check-icon v-if="slotProps?.data?.paid" style="font-size: 1.125rem;" />
      </template>
    </Column>
    <Column header="PDF" body-style="text-align: center; padding:0; width: 3em;" :pt="{ columnHeaderContent: { class: 'justify-center' }}">
      <template #body="slotProps">
        <Button
            v-if="slotProps?.data?.pdfUrl"
            as="a"
            variant="link"
            icon="pi pi-file-pdf"
            aria-label="Download als pdf"
            :href="slotProps?.data?.pdfUrl"
            size="large"
            style="padding:0;"
        /><!-- Wouter: workaround for default button padding blowing up the table row height + needs tooltip -->
      </template>
    </Column>
    <Column header="CSV" body-style="text-align: center; center; padding:0; width: 3em;" :pt="{ columnHeaderContent: { class: 'justify-center' }}">
      <template #body="slotProps">
        <Button
            v-if="showCsv && slotProps?.data?.csvUrl"
            as="a"
            variant="link"
            icon="pi pi-file-excel"
            aria-label="Download als csv"
            :href="slotProps?.data?.csvUrl"
            size="large"
            style="padding:0;"
        />
      </template>
    </Column>
  </aeg-datatable>
</template>

<style scoped lang="scss">

</style>
